<template>
	<div
		class="moment-video-preview"
		:class="{ 'has-frame-aspect-ratio': frameAspectRatio }"
	>
		<div
			class="moment-video-preview__frame"
			:style="frameStyle"
		>
			<video
				v-if="videoUrl"
				ref="video"
				class="moment-video-preview__video"
				:src="videoUrl"
				:poster="poster"
				:muted="muted"
				playsinline
				@loadedmetadata="handleLoadedMetadata"
				@timeupdate="handleTimeUpdate"
				@play="handlePlay"
				@pause="handlePause"
				@volumechange="handleVolumeChange"
				@ended="handleEnded"
			/>
			<div
				v-else-if="showPlaceholder"
				class="moment-video-preview__placeholder"
				:style="poster ? { backgroundImage: `url(${poster})` } : null"
			>
				<LoadingSpinner
					v-if="isLoading"
					color="light"
				/>
			</div>
			<slot
				name="over"
				v-bind="slotProps"
			/>
		</div>
		<slot
			v-bind="slotProps"
		/>
		<slot
			name="after"
			v-bind="slotProps"
		/>
	</div>
</template>

<script>
import LoadingSpinner from '@shared/components/LoadingSpinner';
import { httpClient } from '@shared/js/api/httpClient';

export default {
	name:       'MomentVideoPreview',
	components: { LoadingSpinner },
	props:      {
		eventVideoCropId: {
			type:     [String, Number],
			required: true,
		},
		poster: {
			type:    String,
			default: null,
		},
		loadOnMount: {
			type:    Boolean,
			default: false,
		},
		showPlaceholder: {
			type:    Boolean,
			default: false,
		},
		frameAspectRatio: {
			type:    String,
			default: null,
		},
	},
	data() {
		return {
			videoUrl:        '',
			duration:        0,
			position:        0,
			volume:          0,
			requestedCropId: null,
			isPlaying:       false,
			isMuted:         true,
			isLoading:       false,
			muted:           true,
		};
	},
	computed: {
		frameStyle() {
			if (!this.frameAspectRatio) {
				return null;
			}

			return { aspectRatio: this.frameAspectRatio };
		},
		slotProps() {
			return {
				videoUrl:    this.videoUrl,
				isLoading:   this.isLoading,
				duration:    this.duration,
				position:    this.position,
				isPlaying:   this.isPlaying,
				isMuted:     this.isMuted,
				volume:      this.volume,
				play:        this.play,
				pause:       this.pause,
				reset:       this.reset,
				seek:        this.seek,
				toggleMuted: this.toggleMuted,
				setMuted:    this.setMuted,
				setVolume:   this.setVolume,
			};
		},
	},
	watch: {
		eventVideoCropId() {
			this.reset();
			this.videoUrl = '';
			if (this.loadOnMount) {
				this.loadVideoUrl();
			}
		},
	},
	mounted() {
		if (this.loadOnMount) {
			this.loadVideoUrl();
		}
	},
	beforeDestroy() {
		this.reset();
	},
	methods: {
		async loadVideoUrl() {
			if (!this.eventVideoCropId || this.videoUrl) {
				return;
			}

			this.isLoading = true;
			const requestedCropId = this.eventVideoCropId;
			this.requestedCropId = requestedCropId;
			try {
				const response = await httpClient.getEventVideoCropPlayout(requestedCropId);
				if (requestedCropId !== this.eventVideoCropId) {
					return;
				}
				this.videoUrl = response.data?.videoUrl || '';
			} catch (error) {
				this.$emit('error', error);
			} finally {
				if (requestedCropId === this.eventVideoCropId) {
					this.isLoading = false;
				}
			}
		},
		getVideoElement() {
			return this.$refs.video;
		},
		async play() {
			await this.loadVideoUrl();
			await this.$nextTick();
			await this.getVideoElement()?.play()?.catch(() => {});
		},
		pause() {
			this.getVideoElement()?.pause();
		},
		reset() {
			const video = this.getVideoElement();
			if (video) {
				video.pause();
				video.currentTime = 0;
			}
			this.position = 0;
			this.isPlaying = false;
		},
		seek(position) {
			const video = this.getVideoElement();
			if (video) {
				video.currentTime = Math.max(0, Math.min(this.duration || 0, position));
			}
		},
		toggleMuted() {
			const video = this.getVideoElement();
			if (video) {
				video.muted = !video.muted;
			}
		},
		setMuted(isMuted) {
			const video = this.getVideoElement();
			if (video) {
				video.muted = isMuted;
			}
		},
		setVolume(volume) {
			const video = this.getVideoElement();
			if (video) {
				video.volume = Math.max(0, Math.min(100, volume)) / 100;
				video.muted = volume === 0;
			}
		},
		handleLoadedMetadata() {
			const video = this.getVideoElement();
			this.duration = Number.isFinite(video?.duration) ? video.duration : 0;
			this.handleVolumeChange();
			this.$emit('loadedmetadata', this.duration);
		},
		handleTimeUpdate() {
			const video = this.getVideoElement();
			this.position = Number.isFinite(video?.currentTime) ? video.currentTime : 0;
			this.$emit('timeupdate', this.position);
		},
		handlePlay() {
			this.isPlaying = true;
			this.$emit('is-playing-change', true);
			this.$emit('play');
		},
		handlePause() {
			this.isPlaying = false;
			this.$emit('is-playing-change', false);
			this.$emit('pause');
		},
		handleEnded() {
			this.isPlaying = false;
			this.position = this.duration;
			this.$emit('is-playing-change', false);
			this.$emit('ended');
		},
		handleVolumeChange() {
			const video = this.getVideoElement();
			this.isMuted = Boolean(video?.muted);
			this.volume = Math.round((video?.volume ?? 0) * 100);
		},
	},
};
</script>

<style lang="scss" scoped>
.moment-video-preview {
	width: 100%;
	height: 100%;
}

.moment-video-preview.has-frame-aspect-ratio {
	height: auto;
}

.moment-video-preview__frame {
	position: relative;
	width: 100%;
	height: 100%;
}

.moment-video-preview.has-frame-aspect-ratio .moment-video-preview__frame {
	height: auto;
	overflow: hidden;
}

.moment-video-preview__video,
.moment-video-preview__placeholder {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
}

.moment-video-preview__video {
	object-fit: contain;
}

.moment-video-preview__placeholder {
	display: flex;
	align-items: center;
	justify-content: center;
	background-position: center;
	background-size: cover;
}
</style>
